import React, {
  useContext, useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '@americanexpress/one-app-router';
import { ProgressCircle } from '@americanexpress/dls-react';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { useDispatch } from 'react-redux';
import { TokenContext } from '../utility/TokenContext';
import useApiWithSession from '../utility/useApiWithSession';
import { PaymentContext } from '../utility/PaymentContext';
import Wrapper from '../components/Wrapper';

const Loader = ({ location, router }) => {
  const dispatch = useDispatch();
  const { gateWayUrl } = useContext(TokenContext);
  const {
    sid1, market1, journey1, u1, setEnv, env,
    setSid1, setMarket1, setJourney1, setU1, paymentResponse, setPaymentResponse, setMetaData,
  } = useContext(PaymentContext);

  // API call for START_SESSION (always runs)
  const {
    isLoading: isLoadingStart, data: dataStart, error: errorStart, run: runStartSession,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'START_SESSION' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // API call for MESSAGE_CLICK (only if session_id is missing/empty)
  const {
    isLoading: isLoadingMsg, data: dataMsg, error: errorMsg, run: runMessageClick,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'MESSAGE_CLICK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  const [flag, setFlag] = useState(false);

  // Helper to set session_id cookie
  const setSessionIdInCookies = (sessionId) => {
    if (sessionId) {
      document.cookie = `session_id=${sessionId}; path=/; SameSite=Strict`;
    }
  };

  useEffect(() => {
    const { query } = location;
    const {
      sid, j, u, e,
    } = query;
    if (sid && j && u) {
      const market = j.slice(0, 3);
      const journey = j.slice(3).toLowerCase();
      setSid1(sid);
      setMarket1(market);
      setJourney1(journey);
      setU1(u);
    }
    if (e) {
      setEnv(e);
    }
  }, [location, setSid1, setMarket1, setJourney1]);

  useEffect(() => {
    if (!sid1 || !market1 || !journey1 || !u1) {
      return;
    }
    // Wrap async logic in an inner function to satisfy ESLint
    const runAsync = async () => {
      try {
        // Run MESSAGE_CLICK only if session_id cookie is missing/empty
        if (location?.query?.sid) {
          await runMessageClick();
          setSessionIdInCookies(sid1);
          if (dataMsg?.statusCode === 'failure' || errorMsg) {
            return;
          }
        }
        // Always run START_SESSION
        await runStartSession();
        setFlag(true);
      } catch (err) {
        dispatch(addErrorToReport(err, { message: 'Loader: API call failed.' }));
        router.push({ pathname: '/message', query: { level: 3 } });
      }
    };
    runAsync();
  }, [sid1, market1, journey1, u1]);

  useEffect(() => {
    if (!flag) {
      return;
    }
    // Prefer START_SESSION response if successful
    if (dataStart?.statusCode === 'success' && dataStart?.eventData) {
      setPaymentResponse(dataStart.eventData || null);
      setMetaData(dataStart?.metaData || null);
    }
  }, [flag, dataStart, dataMsg]);

  useEffect(() => {
    if (paymentResponse == null) return;
    if (!isLoadingStart && dataStart?.eventData) {
      if (journey1 === 'pbp') {
        router.push({ pathname: '/payyourbill' });
      } else if (env && journey1 === 'bal') {
        router.push({ pathname: '/balance' });
      }
    }
  }, [paymentResponse, isLoadingStart, isLoadingMsg, dataStart, dataMsg]);

  useEffect(() => {
    if (
      (isLoadingStart === false && (errorStart || dataStart?.statusCode === 'failure'))
      || (isLoadingMsg === false && (errorMsg || dataMsg?.statusCode === 'failure'))
    ) {
      if (isLoadingStart === false && (errorStart || dataStart?.statusCode === 'failure')) {
        dispatch(addErrorToReport(errorStart || new Error('START_SESSION failed'), { message: 'Loader: START_SESSION event failed.' }));
      }
      if (isLoadingMsg === false && (errorMsg || dataMsg?.statusCode === 'failure')) {
        dispatch(addErrorToReport(errorMsg || new Error('MESSAGE_CLICK failed'), { message: 'Loader: MESSAGE_CLICK event failed.' }));
      }
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [isLoadingStart, errorStart, dataStart, isLoadingMsg, errorMsg, dataMsg, dispatch, router]);

  return (
    <Wrapper>
      <div className="margin-4-t margin-2-b">
        <ProgressCircle />
      </div>
    </Wrapper>
  );
};

export default withRouter(Loader);

Loader.propTypes = {
  location: PropTypes.shape({
    query: PropTypes.shape({
      sid: PropTypes.string,
      j: PropTypes.string,
      u: PropTypes.string,
      e: PropTypes.string,
    }).isRequired,
  }).isRequired,
  router: PropTypes.instanceOf(Object).isRequired,
};
